/**
 * Timezone Utilities
 *
 * Provides timezone detection, conversion, and formatting utilities
 * to display dates and times in the visitor's local timezone.
 */

export interface TimezoneInfo {
  timezone: string;
  offset: string;
  abbreviation: string;
}

/**
 * Detect the user's timezone using the Intl API
 */
export const detectUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Failed to detect timezone:', error);
    return 'UTC';
  }
};

/**
 * Get user's preferred timezone from localStorage or auto-detect
 */
export const getUserTimezone = (): string => {
  const stored = localStorage.getItem('userTimezone');
  return stored || detectUserTimezone();
};

/**
 * Save user's preferred timezone to localStorage
 */
export const setUserTimezone = (timezone: string): void => {
  localStorage.setItem('userTimezone', timezone);
};

/**
 * Get timezone information (name, offset, abbreviation)
 */
export const getTimezoneInfo = (timezone?: string): TimezoneInfo => {
  const tz = timezone || getUserTimezone();
  const date = new Date();

  try {
    // Get timezone offset
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    const abbreviation = timeZonePart?.value || '';

    // Calculate offset
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60000;
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const offsetSign = offsetMinutes >= 0 ? '+' : '-';
    const offset = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;

    return {
      timezone: tz,
      offset,
      abbreviation,
    };
  } catch (error) {
    console.error('Failed to get timezone info:', error);
    return {
      timezone: 'UTC',
      offset: '+00:00',
      abbreviation: 'UTC',
    };
  }
};

/**
 * Format date and time in user's timezone
 *
 * @param date - Date string (ISO 8601) or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @param locale - Locale code (en, ar, etc.)
 * @param timezone - Timezone to use (defaults to user's timezone)
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  locale?: string,
  timezone?: string
): string => {
  if (!date) return 'N/A';

  try {
    let dateObj: Date;

    if (typeof date === 'string') {
      // Ensure date string is treated as UTC if no timezone indicator is present
      // Check if the string already has a timezone indicator (Z, +HH:MM, -HH:MM)
      const hasTimezone = date.includes('Z') || /[+-]\d{2}:\d{2}$/.test(date);

      if (!hasTimezone) {
        // Convert space-separated datetime to ISO format and add UTC indicator
        const isoString = date.includes('T') ? date : date.replace(' ', 'T');
        dateObj = new Date(isoString + 'Z');
      } else {
        dateObj = new Date(date);
      }
    } else {
      dateObj = date;
    }

    // Check for invalid date
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    const tz = timezone || getUserTimezone();
    const loc = locale || 'en-US';

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: tz,
    };

    return new Intl.DateTimeFormat(loc, { ...defaultOptions, ...options }).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Format date only (no time) in user's timezone
 */
export const formatDate = (
  date: string | Date | null | undefined,
  locale?: string,
  timezone?: string
): string => {
  return formatDateTime(
    date,
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    locale,
    timezone
  );
};

/**
 * Format time only (no date) in user's timezone
 */
export const formatTime = (
  date: string | Date | null | undefined,
  locale?: string,
  timezone?: string
): string => {
  return formatDateTime(
    date,
    {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    },
    locale,
    timezone
  );
};

/**
 * Format relative time ("2 hours ago") in user's timezone
 */
export const formatRelativeTime = (
  date: string | Date | null | undefined,
  locale?: string
): string => {
  if (!date) return 'N/A';

  try {
    const now = new Date();
    let targetDate: Date;

    if (typeof date === 'string') {
      // Ensure date string is treated as UTC if no timezone indicator is present
      const hasTimezone = date.includes('Z') || /[+-]\d{2}:\d{2}$/.test(date);

      if (!hasTimezone) {
        const isoString = date.includes('T') ? date : date.replace(' ', 'T');
        targetDate = new Date(isoString + 'Z');
      } else {
        targetDate = new Date(date);
      }
    } else {
      targetDate = date;
    }

    if (isNaN(targetDate.getTime())) {
      return 'Invalid Date';
    }

    const diffInSeconds = Math.floor((now.getTime() - targetDate.getTime()) / 1000);

    const loc = locale || 'en-US';

    // Less than 1 minute
    if (diffInSeconds < 60) {
      return loc === 'ar' ? 'الآن' : 'Just now';
    }

    // Less than 1 hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      if (loc === 'ar') {
        return `منذ ${minutes} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`;
      }
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    // Less than 24 hours
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      if (loc === 'ar') {
        return `منذ ${hours} ${hours === 1 ? 'ساعة' : 'ساعات'}`;
      }
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }

    // Less than 30 days
    if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      if (loc === 'ar') {
        return `منذ ${days} ${days === 1 ? 'يوم' : 'أيام'}`;
      }
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }

    // More than 30 days - show full date
    return formatDate(targetDate, loc);
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid Date';
  }
};

/**
 * Format full date with time and timezone indicator
 */
export const formatFullDateTime = (
  date: string | Date | null | undefined,
  locale?: string,
  showTimezone: boolean = true
): string => {
  if (!date) return 'N/A';

  const formatted = formatDateTime(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }, locale);

  if (showTimezone) {
    const tzInfo = getTimezoneInfo();
    return `${formatted} (${tzInfo.abbreviation})`;
  }

  return formatted;
};

/**
 * List of common timezones with friendly names
 */
export const COMMON_TIMEZONES = [
  { value: 'Asia/Riyadh', label: 'Riyadh (Saudi Arabia)', labelAr: 'الرياض (السعودية)' },
  { value: 'Asia/Dubai', label: 'Dubai (UAE)', labelAr: 'دبي (الإمارات)' },
  { value: 'Asia/Kuwait', label: 'Kuwait', labelAr: 'الكويت' },
  { value: 'Asia/Qatar', label: 'Doha (Qatar)', labelAr: 'الدوحة (قطر)' },
  { value: 'Asia/Bahrain', label: 'Bahrain', labelAr: 'البحرين' },
  { value: 'Asia/Muscat', label: 'Muscat (Oman)', labelAr: 'مسقط (عمان)' },
  { value: 'Africa/Cairo', label: 'Cairo (Egypt)', labelAr: 'القاهرة (مصر)' },
  { value: 'Asia/Amman', label: 'Amman (Jordan)', labelAr: 'عمان (الأردن)' },
  { value: 'Asia/Beirut', label: 'Beirut (Lebanon)', labelAr: 'بيروت (لبنان)' },
  { value: 'Asia/Damascus', label: 'Damascus (Syria)', labelAr: 'دمشق (سوريا)' },
  { value: 'Asia/Baghdad', label: 'Baghdad (Iraq)', labelAr: 'بغداد (العراق)' },
  { value: 'America/New_York', label: 'New York (USA - Eastern)', labelAr: 'نيويورك (الولايات المتحدة)' },
  { value: 'America/Chicago', label: 'Chicago (USA - Central)', labelAr: 'شيكاغو (الولايات المتحدة)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (USA - Pacific)', labelAr: 'لوس أنجلوس (الولايات المتحدة)' },
  { value: 'Europe/London', label: 'London (UK)', labelAr: 'لندن (المملكة المتحدة)' },
  { value: 'Europe/Paris', label: 'Paris (France)', labelAr: 'باريس (فرنسا)' },
  { value: 'Europe/Berlin', label: 'Berlin (Germany)', labelAr: 'برلين (ألمانيا)' },
  { value: 'Asia/Singapore', label: 'Singapore', labelAr: 'سنغافورة' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', labelAr: 'هونغ كونغ' },
  { value: 'Asia/Tokyo', label: 'Tokyo (Japan)', labelAr: 'طوكيو (اليابان)' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia)', labelAr: 'سيدني (أستراليا)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', labelAr: 'التوقيت العالمي المنسق' },
];

/**
 * Get timezone label in the specified language
 */
export const getTimezoneLabel = (timezone: string, locale: string = 'en'): string => {
  const tz = COMMON_TIMEZONES.find(t => t.value === timezone);
  if (!tz) return timezone;

  return locale === 'ar' ? tz.labelAr : tz.label;
};

/**
 * Convert UTC date string to user's local timezone
 */
export const utcToLocal = (utcDate: string | Date): Date => {
  let date: Date;

  if (typeof utcDate === 'string') {
    // Ensure date string is treated as UTC if no timezone indicator is present
    const hasTimezone = utcDate.includes('Z') || /[+-]\d{2}:\d{2}$/.test(utcDate);

    if (!hasTimezone) {
      const isoString = utcDate.includes('T') ? utcDate : utcDate.replace(' ', 'T');
      date = new Date(isoString + 'Z');
    } else {
      date = new Date(utcDate);
    }
  } else {
    date = utcDate;
  }

  const timezone = getUserTimezone();

  // Create a date string in the user's timezone
  const localString = date.toLocaleString('en-US', { timeZone: timezone });
  return new Date(localString);
};

/**
 * Check if a date is today in user's timezone
 */
export const isToday = (date: string | Date): boolean => {
  let targetDate: Date;

  if (typeof date === 'string') {
    // Ensure date string is treated as UTC if no timezone indicator is present
    const hasTimezone = date.includes('Z') || /[+-]\d{2}:\d{2}$/.test(date);

    if (!hasTimezone) {
      const isoString = date.includes('T') ? date : date.replace(' ', 'T');
      targetDate = new Date(isoString + 'Z');
    } else {
      targetDate = new Date(date);
    }
  } else {
    targetDate = date;
  }

  const today = new Date();
  const timezone = getUserTimezone();

  const targetDay = new Date(targetDate.toLocaleString('en-US', { timeZone: timezone })).toDateString();
  const todayDay = new Date(today.toLocaleString('en-US', { timeZone: timezone })).toDateString();

  return targetDay === todayDay;
};

/**
 * Check if a date is yesterday in user's timezone
 */
export const isYesterday = (date: string | Date): boolean => {
  let targetDate: Date;

  if (typeof date === 'string') {
    // Ensure date string is treated as UTC if no timezone indicator is present
    const hasTimezone = date.includes('Z') || /[+-]\d{2}:\d{2}$/.test(date);

    if (!hasTimezone) {
      const isoString = date.includes('T') ? date : date.replace(' ', 'T');
      targetDate = new Date(isoString + 'Z');
    } else {
      targetDate = new Date(date);
    }
  } else {
    targetDate = date;
  }

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const timezone = getUserTimezone();

  const targetDay = new Date(targetDate.toLocaleString('en-US', { timeZone: timezone })).toDateString();
  const yesterdayDay = new Date(yesterday.toLocaleString('en-US', { timeZone: timezone })).toDateString();

  return targetDay === yesterdayDay;
};
